import BaseDropdownController from './base_dropdown_controller'
import { formatNumber, parseCleaveInput } from "../helpers/format-helper"

export default class extends BaseDropdownController {
  static targets = ["fromInput", "toInput", "applyBtn", "dropdownToggle", "countDisplay"]

  connect() {
    super.connect()
    this.initializeFromFormState()
    this.updateApplyButton()
    this.updateCountDisplay()
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
    
    // Listen for turbo frame loads to update display after form submissions
    document.addEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))
  }

  disconnect() {
    super.disconnect()
    this.element.removeEventListener('keydown', this.handleKeydown.bind(this))
    document.removeEventListener('turbo:frame-load', this.handleTurboFrameLoad.bind(this))
  }

  handleTurboFrameLoad(event) {
    // Update display when the auction results frame loads (after form submission)
    if (event.target.id === 'auction_results') {
      setTimeout(() => {
        this.updateCountDisplay()
      }, 50) // Small delay to ensure form is fully updated
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter" && this.dropdownToggleTarget.getAttribute('aria-expanded') === 'true') {
      event.preventDefault()
      this.applyFilters()
    }
  }

  initializeFromFormState() {
    const form = document.getElementById('auction-filters-form')
    if (form) {
      const minValueDifferenceField = form.querySelector('[name="min_value_difference"]')
      const maxValueDifferenceField = form.querySelector('[name="max_value_difference"]')
      
      if (minValueDifferenceField && minValueDifferenceField.value) {
        this.fromInputTarget.value = minValueDifferenceField.value
      }
      
      if (maxValueDifferenceField && maxValueDifferenceField.value) {
        this.toInputTarget.value = maxValueDifferenceField.value
      }
    }
  }

  onInputChange(event) {
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  onInputBlur(event) {
    this.validateValueDifferenceRange(event.target)
    this.updateApplyButton()
    this.updateCountDisplay()
  }

  validateValueDifferenceRange(changedInput) {
    const MIN_VALUE = -999
    const MAX_VALUE = 999

    let fromValue = parseCleaveInput(this.fromInputTarget.value)
    let toValue = parseCleaveInput(this.toInputTarget.value)

    if (Number.isFinite(fromValue)) {
      fromValue = Math.max(MIN_VALUE, Math.min(MAX_VALUE, fromValue))
      this.fromInputTarget.value = fromValue
    } else {
      fromValue = null
    }

    if (Number.isFinite(toValue)) {
      toValue = Math.max(MIN_VALUE, Math.min(MAX_VALUE, toValue))
      this.toInputTarget.value = toValue
    } else {
      toValue = null
    }

    if (fromValue !== null && toValue !== null && fromValue > toValue) {
      if (changedInput === this.fromInputTarget) {
        this.toInputTarget.value = fromValue
      } else if (changedInput === this.toInputTarget) {
        this.fromInputTarget.value = toValue
      }
    }
  }

  applyFilters() {
    const focusedInput = document.activeElement
    if (focusedInput === this.fromInputTarget || focusedInput === this.toInputTarget) {
      this.validateValueDifferenceRange(focusedInput)
      this.updateApplyButton()
      this.updateCountDisplay()
    }
    
    this.syncWithFormFields()
    this.closeDropdown()
    this.closeOffcanvas()
    this.submitSharedForm()
  }

  syncWithFormFields() {
    const form = document.getElementById('auction-filters-form')
    if (form) {
      const minValueDifferenceField = form.querySelector('[name="min_value_difference"]')
      const maxValueDifferenceField = form.querySelector('[name="max_value_difference"]')
      
      if (minValueDifferenceField) {
        minValueDifferenceField.value = this.fromInputTarget.value
      }
      
      if (maxValueDifferenceField) {
        maxValueDifferenceField.value = this.toInputTarget.value
      }
    }
  }

  clearFilters() {
    this.fromInputTarget.value = ''
    this.toInputTarget.value = ''
    
    this.syncWithFormFields()
    this.updateApplyButton()
    this.updateCountDisplay()
    this.submitSharedForm()
  }

  updateApplyButton() {
    if (!this.hasApplyBtnTarget) return
    
    const fromValue = this.fromInputTarget.value.trim()
    const toValue = this.toInputTarget.value.trim()
    
    this.applyBtnTarget.disabled = fromValue === '' && toValue === ''
  }

  updateCountDisplay() {
    if (!this.hasCountDisplayTarget) return

    setTimeout(() => {
      // Get applied values from form fields instead of current input values
      const form = document.getElementById('auction-filters-form')
      let fromValue = null
      let toValue = null
      
      if (form) {
        const minValueDifferenceField = form.querySelector('[name="min_value_difference"]')
        const maxValueDifferenceField = form.querySelector('[name="max_value_difference"]')
        fromValue = minValueDifferenceField ? parseCleaveInput(minValueDifferenceField.value) : null
        toValue = maxValueDifferenceField ? parseCleaveInput(maxValueDifferenceField.value) : null
      }

      const hasRange = fromValue !== null || toValue !== null

      if (!hasRange) {
        this.countDisplayTarget.textContent = ""
      } else {
        let rangeText = ''
        if (fromValue !== null && toValue !== null) {
          rangeText = `(${formatNumber(fromValue)}% - ${formatNumber(toValue)}%)`
        } else if (fromValue !== null) {
          rangeText = `(≥ ${formatNumber(fromValue)}%)`
        } else if (toValue !== null) {
          rangeText = `(≤ ${formatNumber(toValue)}%)`
        }
        this.countDisplayTarget.textContent = rangeText
      }

      if (this.hasDropdownToggleTarget) {
        if (!hasRange) {
          this.dropdownToggleTarget.classList.remove('filter-dropdown-active')
        } else {
          this.dropdownToggleTarget.classList.add('filter-dropdown-active')
        }
      }
    }, 0)
  }
}
